import { useEffect, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { format, parseISO, isToday, isTomorrow } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Loader2, CalendarIcon, Clock, MapPin, CalendarPlus } from "lucide-react";

const addToCalendarHref = (eventId) => {
    const token = localStorage.getItem("ccc_token") || "";
    return `${API_BASE}/events/${eventId}/ics?token=${encodeURIComponent(token)}`;
};

export default function ParentEvents() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/events").then((r) => setEvents(r.data.events)).finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

    return (
        <div className="space-y-6" data-testid="parent-events-page">
            <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Save the date</div>
                <h1 className="font-heading text-3xl font-black tracking-tight mt-1">Upcoming Events</h1>
                <p className="text-muted-foreground mt-1">Everything happening soon at Childhood. You&apos;ll get a reminder the day before.</p>
            </div>

            {events.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-3xl">
                    <CalendarIcon className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">No events scheduled yet.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {events.map((ev) => {
                        const d = parseISO(ev.event_date);
                        const badge = isToday(d) ? "Today" : isTomorrow(d) ? "Tomorrow" : null;
                        return (
                            <Card key={ev.id} className="rounded-2xl overflow-hidden" data-testid={`parent-event-${ev.id}`}>
                                <CardHeader>
                                    <div className="flex items-start gap-4">
                                        <div className="shrink-0 w-20 rounded-2xl bg-primary text-primary-foreground flex flex-col items-center justify-center py-3">
                                            <div className="text-xs font-bold uppercase tracking-widest">{format(d, "MMM")}</div>
                                            <div className="text-3xl font-black font-heading leading-none">{format(d, "d")}</div>
                                            <div className="text-[10px] uppercase tracking-wider mt-1 opacity-80">{format(d, "EEE")}</div>
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <CardTitle className="font-heading text-xl">{ev.title}</CardTitle>
                                                {badge && <Badge className="rounded-full">{badge}</Badge>}
                                            </div>
                                            <CardDescription>{format(d, "EEEE, d MMMM yyyy")}</CardDescription>
                                            <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                                                {ev.event_time && (
                                                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {ev.event_time}</span>
                                                )}
                                                {ev.location && (
                                                    <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {ev.location}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </CardHeader>
                                {ev.description && (
                                    <CardContent className="pb-3">
                                        <div className="notif-prose text-sm" dangerouslySetInnerHTML={{ __html: ev.description }} />
                                    </CardContent>
                                )}
                                <CardContent className="pt-0">
                                    <Button
                                        asChild
                                        variant="outline"
                                        size="sm"
                                        className="rounded-full"
                                        data-testid={`add-to-calendar-${ev.id}`}
                                    >
                                        <a href={addToCalendarHref(ev.id)} download>
                                            <CalendarPlus className="h-3.5 w-3.5 mr-2" /> Add to Calendar
                                        </a>
                                    </Button>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
